import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import { Link } from 'react-router';
import { getBookingFromContracts } from '../../app-bookings-request/forms/bookingUtil';
import { BookingSummaryDateTime } from '../common/items/BookingSummaryDateTime';
import { BookingSummaryItem } from '../common/items/BookingSummaryItem';
import { checkIsGuestView } from '../common/utils/check-is-guest-view';
import { BookingDetailsBtn } from './items/BookingDetailsBtn';
import { BookingSummaryActivity } from './items/BookingSummaryActivity';
import { BookingSummaryAddress } from './items/BookingSummaryAddress';
import { BookingSummaryAlcohol } from './items/BookingSummaryAlcohol';
import { BookingSummaryAttendees } from './items/BookingSummaryAttendees';
import { BookingSummarySpace } from './items/BookingSummarySpace';
import { BookingSummaryUser } from './items/BookingSummaryUser';
import { BookingSummaryPrice } from './items/booking-summary-price/BookingSummaryPrice';
import { getBookingSummaryTitle } from './utils/get-booking-summary-title';
import { shouldShowAddress } from './utils/should-show-address';
import { shouldShowBookingDetailsBtn } from './utils/should-show-booking-details-btn';
import { shouldShowContactInfo } from './utils/should-show-contact-info';
import { shouldShowManagePayments } from './utils/should-show-manage-payments';
import { shouldShowPriceCalc } from './utils/should-show-price-calc';

export const BookingSummary = ({
  booking,
  currentContracts,
  pendingContracts,
  profile,
  intl,
  iosWebViewConfig,
  style
}) => {
  const isGuestView = checkIsGuestView(profile, booking);
  const displayedUser = isGuestView ? booking.host : booking.guest;
  const title = getBookingSummaryTitle(booking, intl);
  const pendingBooking = getBookingFromContracts(pendingContracts);

  return (
    <div
      className='border rounded-border margin-no-top margin-lg-bottom padding-xl-left padding-xl-right'
      style={{ paddingTop: '40px', ...style }}
      data-testing-id='booking-summary'
    >
      <h4
        className='margin-no-top margin-no-bottom'
        style={{ fontSize: '20px' }}
        data-testing-id='booking-summary-title'
      >
        {title}
      </h4>

      <div className='margin-md-top'>
        <BookingSummaryUser
          booking={booking}
          profile={profile}
          iosWebViewConfig={iosWebViewConfig}
        />
      </div>

      {shouldShowContactInfo(booking) && displayedUser?.phone && (
        <div
          className='margin-md-top'
          data-testing-id='booking-summary-contact-info'
        >
          <BookingSummaryItem
            label={
              <FormattedMessage
                id='inbox.summary_props.contact_info'
                defaultMessage='Contact Information'
              />
            }
          >
            {displayedUser.phone}
          </BookingSummaryItem>
        </div>
      )}

      {shouldShowAddress(booking, isGuestView) && (
        <div className='margin-md-top'>
          <BookingSummaryAddress booking={booking} />
        </div>
      )}

      <div className='margin-md-top'>
        <BookingSummaryDateTime
          currentBooking={booking}
          pendingBooking={pendingBooking}
          profile={profile}
        />
      </div>

      <div className='margin-md-top'>
        <BookingSummaryAttendees
          currentBooking={booking}
          pendingBooking={pendingBooking}
        />
      </div>

      {!!booking?.tags?.activity && !iosWebViewConfig?.isWebView && (
        <div className='margin-md-top'>
          <BookingSummaryActivity booking={booking} />
        </div>
      )}

      <div className='margin-md-top'>
        <BookingSummaryAlcohol
          currentBooking={booking}
          pendingBooking={pendingBooking}
        />
      </div>

      {shouldShowPriceCalc(booking, currentContracts, pendingContracts) && (
        <div className='margin-md-top'>
          <BookingSummaryPrice
            currentBooking={booking}
            currentContracts={currentContracts}
            pendingBooking={pendingBooking}
            pendingContracts={pendingContracts}
            profile={profile}
          />
        </div>
      )}

      {shouldShowManagePayments(booking, isGuestView) && (
        <div
          className='margin-md-top'
          data-testing-id='booking-summary-manage-payments-btn'
        >
          <Link
            to={`/inbox/${booking.booking_id || booking._id}/payment-details`}
            className='btn btn-default full-width text-wrap'
          >
            <FormattedMessage
              id='inbox.summary_props.view_manage_payments'
              defaultMessage='View and Manage Payments'
            />
          </Link>
        </div>
      )}

      {shouldShowBookingDetailsBtn(booking, isGuestView) && (
        <div
          className='margin-md-top'
          data-testing-id='booking-summary-details-btn'
        >
          <BookingDetailsBtn
            booking={booking}
            profile={profile}
            iosWebViewConfig={iosWebViewConfig}
          />
        </div>
      )}

      <div className='margin-md-top'>
        <BookingSummarySpace
          booking={booking}
          iosWebViewConfig={iosWebViewConfig}
          isGuestView={isGuestView}
        />
      </div>
    </div>
  );
};

BookingSummary.propTypes = {
  booking: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  currentContracts: PropTypes.arrayOf(PropTypes.object),
  pendingContracts: PropTypes.arrayOf(PropTypes.object),
  intl: intlShape,
  iosWebViewConfig: PropTypes.shape({
    isWebView: PropTypes.bool
  }),
  style: PropTypes.object
};
